import type { Attribute, AttributeGroup } from "./types";
import {
  ALL_ATTRIBUTES_GROUP_ID,
  helperClass,
  inputClass,
  labelClass,
  sectionCard,
} from "./constants";

type AttributesSectionProps = {
  selectedAttributeGroupId: string;
  setSelectedAttributeGroupId: (id: string) => void;
  attributeSearch: string;
  setAttributeSearch: (query: string) => void;
  attributeGroups: AttributeGroup[];
  attributes: Attribute[];
  selectedAttributeIds: string[];
  selectedAttributeNames: string[];
  visibleAttributeGroups: AttributeGroup[];
  attributesByGroupId: Map<string, Attribute[]>;
  toggleAttribute: (attributeId: string) => void;
  setAttributesSelected: (attributeIds: string[], selected: boolean) => void;
};

export const AttributesSection = ({
  selectedAttributeGroupId,
  setSelectedAttributeGroupId,
  attributeSearch,
  setAttributeSearch,
  attributeGroups,
  attributes,
  selectedAttributeIds,
  selectedAttributeNames,
  visibleAttributeGroups,
  attributesByGroupId,
  toggleAttribute,
  setAttributesSelected,
}: AttributesSectionProps) => (
  <section className={sectionCard}>
    <h2 className="text-xl font-semibold text-[#5c2f2f] mb-4">
      Product Attributes
    </h2>

    <div className="mb-4">
      <label className={labelClass}>Attribute Group</label>
      <select
        value={selectedAttributeGroupId}
        onChange={(e) => {
          setSelectedAttributeGroupId(e.target.value);
          setAttributeSearch("");
        }}
        className={inputClass}
      >
        <option value="">Select attribute group</option>
        <option value={ALL_ATTRIBUTES_GROUP_ID}>All Attributes</option>
        {attributeGroups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
      <p className={helperClass}>
        Choose a specific group or All Attributes to search across every option.
      </p>
    </div>

    {selectedAttributeGroupId && (
      <div className="mb-4">
        <label className={labelClass}>Search Attributes</label>
        <input
          type="search"
          placeholder={
            selectedAttributeGroupId === ALL_ATTRIBUTES_GROUP_ID
              ? "Search all attributes"
              : "Search options in this group"
          }
          value={attributeSearch}
          onChange={(e) => setAttributeSearch(e.target.value)}
          className={inputClass}
        />
      </div>
    )}

    {selectedAttributeNames.length > 0 && (
      <div className="mb-4">
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Selected Attributes
        </p>
        <div className="flex flex-wrap gap-2">
          {selectedAttributeNames.map((selectedName) => (
            <span
              key={selectedName}
              className="inline-flex items-center gap-2 rounded-full border border-[#e7d6cf] bg-[#f4ece8] px-3 py-1 text-sm text-[#5c2f2f]"
            >
              {selectedName}
              <button
                type="button"
                onClick={() => {
                  const attribute = attributes.find(
                    (attr) => attr.name === selectedName
                  );
                  if (attribute) toggleAttribute(attribute.id);
                }}
                className="rounded-full text-[#5c2f2f] hover:text-red-600"
                aria-label={`Remove ${selectedName}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      </div>
    )}

    {!selectedAttributeGroupId ? (
      <p className="text-sm text-gray-500">
        Select an attribute group to see its attributes.
      </p>
    ) : visibleAttributeGroups.length === 0 ? (
      <p className="text-sm text-gray-500">No attribute groups found.</p>
    ) : (
      <div className="space-y-4">
        {visibleAttributeGroups.map((group) => {
          const groupAttributes = attributesByGroupId.get(group.id) || [];
          const normalizedSearch = attributeSearch.trim().toLowerCase();
          const visibleAttributes = normalizedSearch
            ? groupAttributes.filter((attribute) =>
                attribute.name.toLowerCase().includes(normalizedSearch)
              )
            : groupAttributes;
          const visibleAttributeIds = visibleAttributes.map(
            (attribute) => attribute.id
          );
          const allVisibleSelected =
            visibleAttributeIds.length > 0 &&
            visibleAttributeIds.every((id) => selectedAttributeIds.includes(id));

          return (
            <div
              key={group.id}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-semibold text-gray-800">{group.name}</h3>
                {groupAttributes.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setAttributesSelected(visibleAttributeIds, !allVisibleSelected)
                    }
                    disabled={visibleAttributeIds.length === 0}
                    className="self-start rounded-lg border border-[#e7d6cf] bg-white px-3 py-2 text-sm font-semibold text-[#5c2f2f] hover:bg-[#f4ece8] disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
                  >
                    {allVisibleSelected ? "Clear Shown" : "Select All Shown"}
                  </button>
                )}
              </div>

              {groupAttributes.length === 0 ? (
                <p className="text-sm text-gray-500">No options yet.</p>
              ) : visibleAttributes.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No attributes match your search.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {visibleAttributes.map((attribute) => (
                    <label
                      key={attribute.id}
                      className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm hover:border-[#5c2f2f]/40"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAttributeIds.includes(attribute.id)}
                        onChange={() => toggleAttribute(attribute.id)}
                      />
                      <span>{attribute.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </section>
);
